import { BirdiTheme, next } from '@birdi/theme';
import { UpgradeIndicator } from './upgrade-indicator';
import React, { useMemo, useState } from 'react';
import { css } from '@emotion/react';

export interface Tab {
  id: string;
  label: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  disabledId?: string;
  disabledTooltipMsg?: string;
  content: () => React.ReactNode;
}

export interface TabsProps {
  tabs: Tab[];
  showHeader?: boolean;
  disableAll?: boolean;
  variant?: next.tabs.TabVariant;
}

export const Tabs = ({
  tabs,
  showHeader = true,
  disableAll = false,
  variant = 'small',
}: TabsProps) => {
  const [activeIndex, setActiveIndex] = useState(() => {
    const firstNonDisabledIndex = tabs.findIndex((tab) => !tab.disabled);
    return firstNonDisabledIndex !== -1 ? firstNonDisabledIndex : 0;
  });
  const activeTab = useMemo(() => tabs[activeIndex], [activeIndex, tabs]);

  return (
    <>
      {showHeader && (
        <ul css={next.tabs.root(variant, tabs.length)}>
          {tabs.map((tab, index) => (
            <li
              css={css`
                display: contents;
              `}
              key={tab.id}
            >
              <button
                css={next.tabs.tab(variant, activeIndex === index)}
                disabled={tab.disabled || disableAll}
                id={tab.disabled ? tab.disabledId : undefined}
                data-testid={`${tab.id}-tab`}
                onClick={() => setActiveIndex(index)}
                aria-label={tab.ariaLabel}
              >
                {tab.label}
                {tab.disabled && tab.disabledTooltipMsg && (
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      margin-left: 0.5rem;
                    `}
                  >
                    <UpgradeIndicator message={tab.disabledTooltipMsg} />
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* Keyed Fragment so the subtree only remounts on tab switch (id
          change), not on every parent re-render. The previous `<Content />`
          form treated the inline arrow function callers pass in as a
          component type — every parent render produced a new function ref,
          which React saw as a new component type and tore the entire tab
          subtree (and its state) down. */}
      <React.Fragment key={activeTab.id}>{activeTab.content()}</React.Fragment>
    </>
  );
};
